import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  CircularProgress,
  Box,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Fade,
  Grow,
  Alert,
  Divider
} from '@mui/material';
import { 
  FaUser, 
  FaLock, 
  FaEye, 
  FaEyeSlash,
  FaQrcode,
  FaBuilding,
  FaShieldAlt
} from 'react-icons/fa';
import backgroundImage from '../assets/intro.png';
import logo from '../assets/logo.png';
import logoImage from '../assets/logo.png';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import QrLoginModal from './QrLoginModal';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  
  const apiBaseURL = 'https://plasticemployers.duckdns.org';

  useEffect(() => {
    axios.get(`${apiBaseURL}/api/set-csrf/`, { withCredentials: true })
      .then(response => {
        console.log(response);
      })
      .catch(error => console.error('Erro ao definir o CSRF token:', error));
      
    const token = localStorage.getItem('access');
    if (token) {
      console.log('Usuário já autenticado, token disponível.');
    }
  }, []);

  function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.startsWith(`${name}=`)) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }
  
  const csrftoken = getCookie('csrftoken');
  console.log("CSRF Token extraído do cookie:", csrftoken);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);
    
    if (!username || !password) {
      setError("Username e password são obrigatórios.");
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.post(`${apiBaseURL}/api/token/`, { username, password }, {
        headers: { 'X-CSRFToken': csrftoken },
        withCredentials: true,
      });
      
      localStorage.setItem('access', response.data.access);
      localStorage.setItem('refresh', response.data.refresh);
      
      const userResponse = await axios.get(`${apiBaseURL}/api/me/`, {
        headers: { Authorization: `Bearer ${response.data.access}` },
        withCredentials: true,
      });
      
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      setSuccessMessage("Login realizado com sucesso! Redirecionando...");
      
      setTimeout(() => {
        navigate('/dashboard', { state: { user: userResponse.data } });
      }, 1500);
      
    } catch (error) {
      setLoading(false);
      if (error.response && error.response.status === 401) {
        setError("Credenciais inválidas. Verifique o username e a password.");
      } else {
        setError("Ocorreu um erro ao tentar fazer login. Tente novamente mais tarde.");
      }
    }
  };

  const handleQrScanSuccess = async (qrDataRaw) => {
    console.log("Dados brutos do QR Code:", qrDataRaw);
  
    // Normalização dos caracteres do QR Code
    const qrData = qrDataRaw.replace(/\?/g, '_').replace(/'/g, '-');
    console.log("QR Code corrigido:", qrData);
    
    try {
      const response = await axios.post(
        `${apiBaseURL}/api/qr-login/`,
        { qr_data: qrData },
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        }
      );
      
      localStorage.setItem('access', response.data.access);
      localStorage.setItem('refresh', response.data.refresh);
      
      const userResponse = await axios.get(`${apiBaseURL}/api/me/`, {
        headers: { Authorization: `Bearer ${response.data.access}` },
        withCredentials: true,
      });
      
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      setSuccessMessage("Login via QR Code realizado com sucesso! Redirecionando...");
      
      setTimeout(() => {
        navigate('/dashboard', { state: { user: userResponse.data } });
      }, 1500);
      
    } catch (error) {
      setError("Erro no login via QR Code. Tente novamente.");
      console.error(error);
    }
  };

  const handleQrLogin = () => {
    setQrModalOpen(true);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.1,
          zIndex: 0,
        }
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          animation: 'float 6s ease-in-out infinite',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          right: '15%',
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.08)',
          animation: 'float 8s ease-in-out infinite reverse',
          zIndex: 0,
        }}
      />

      <Fade in={true} timeout={1000}>
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Grow in={true} timeout={1200}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: 4,
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: 'linear-gradient(90deg, #667eea, #764ba2, #667eea)',
                  backgroundSize: '200% 100%',
                  animation: 'gradient 3s ease infinite',
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                         <Box
                       sx={{
                         width: 60,
                         height: 60,
                         borderRadius: '50%',
                         background: 'linear-gradient(135deg, #667eea, #764ba2)',
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                         mr: 2
                       }}
                     >
                       <img 
                         src={logoImage} 
                         alt="Logo" 
                         style={{ 
                           width: '35px', 
                           height: '35px', 
                           filter: 'brightness(0) invert(1)',
                           objectFit: 'contain',
                           zIndex: 1,
                           position: 'relative'
                         }}
                         onError={(e) => {
                           console.error('Erro ao carregar logo:', e);
                           e.target.style.display = 'none';
                         }}
                         onLoad={() => console.log('Logo carregado com sucesso')}
                       />
                       {/* Fallback se o logo não carregar */}
                       <Box
                         sx={{
                           position: 'absolute',
                           width: '35px',
                           height: '35px',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           color: 'white',
                           fontSize: '20px',
                           fontWeight: 'bold'
                         }}
                       >
                         PD
                       </Box>
                     </Box>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 'bold',
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      PlasticosDão
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Portal do Trabalhador
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                    Faça login para acessar sua conta
                  </Typography>
                </Box>

                {/* Alerts */}
                {error && (
                  <Fade in={true}>
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                      {error}
                    </Alert>
                  </Fade>
                )}
                
                {successMessage && (
                  <Fade in={true}>
                    <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                      {successMessage}
                    </Alert>
                  </Fade>
                )}

                {/* Login Form */}
                <form onSubmit={handleLogin}>
                  <TextField
                    label="Username"
                    fullWidth
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaUser style={{ color: '#667eea' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <TextField
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaLock style={{ color: '#667eea' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            edge="end"
                            sx={{ color: '#667eea' }}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading || !username || !password}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8, #6a4190)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)',
                      },
                      '&:disabled': {
                        background: 'linear-gradient(135deg, #ccc, #999)',
                        transform: 'none',
                        boxShadow: 'none',
                      }
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : (
                      'Entrar'
                    )}
                  </Button>
                </form>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    ou
                  </Typography>
                </Divider>

                {/* QR Code Login Button */}
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleQrLogin}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    borderColor: '#667eea',
                    color: '#667eea',
                    fontSize: '1rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#5a6fd8',
                      backgroundColor: 'rgba(102, 126, 234, 0.04)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                  startIcon={<FaQrcode />}
                >
                  Login com QR Code
                </Button>

                {/* Footer */}
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <FaBuilding style={{ color: '#667eea', marginRight: 8 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Sistema de Gestão Empresarial
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaShieldAlt style={{ color: '#667eea', marginRight: 8 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Acesso Seguro e Criptografado
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grow>
        </Container>
      </Fade>

      <QrLoginModal 
        open={qrModalOpen} 
        onClose={() => setQrModalOpen(false)} 
        onScanSuccess={handleQrScanSuccess}
      />

      {/* CSS Animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          
          @keyframes gradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
    </Box>
  );
};

export default Login;
